/**
 * HYPSEYA — Integration Circles
 * Anonymous collective intelligence: people on the same 8-week program
 * share distilled insights — not personal stories, just generative wisdom.
 * GPT-4o extracts the universally resonant core from each reflection.
 * "Someone in the Grief program wrote something that might resonate with you."
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  GriefIcon, AnxietyIcon, BurnoutIcon,
  IntegrationIcon, RedesignIcon,
} from '@/components/HypIcons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEAL  = '#2D8B87';
const DARK  = '#1E6B68';
const AMBER = '#B8906A';
const CREAM = 'rgba(247,243,236,0.97)';

const PROGRAM_META = {
  grief:       { label: 'Grief & Loss',        short: 'Grief',       color: '#4A7B9D', Icon: GriefIcon       },
  anxiety:     { label: 'Anxiety & Overwhelm', short: 'Anxiety',     color: '#2D8B87', Icon: AnxietyIcon     },
  burnout:     { label: 'Burnout Recovery',    short: 'Burnout',     color: '#B8906A', Icon: BurnoutIcon     },
  integration: { label: 'Trauma Integration',  short: 'Integration', color: '#7B68A0', Icon: IntegrationIcon },
  redesign:    { label: 'Redesign Your Mind',  short: 'Redesign',    color: '#1E6B68', Icon: RedesignIcon    },
};

// ── Insight card ─────────────────────────────────────────────────────────────
function InsightCard({ insight, onUpvote, upvoted }) {
  const dt = new Date(insight.created_at);
  const daysAgo = Math.floor((Date.now() - dt) / 86400000);
  const timeLabel = daysAgo === 0 ? 'today' : daysAgo === 1 ? 'yesterday' : `${daysAgo}d ago`;

  return (
    <div style={{
      background: CREAM,
      border: '1px solid rgba(30,107,104,0.10)',
      borderRadius: '18px',
      padding: '20px',
      marginBottom: '12px',
      boxShadow: '0 2px 14px rgba(30,107,104,0.06)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={{
          fontSize: '10px', fontWeight: 700, color: TEAL,
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>Week {insight.week_number}</span>
        <span style={{ fontSize: '11px', color: '#A0B4B0' }}>{timeLabel}</span>
      </div>

      <p style={{
        margin: '0 0 14px',
        fontSize: '15px',
        lineHeight: '1.75',
        color: '#1A2E2C',
        fontStyle: 'italic',
        fontWeight: 400,
      }}>
        "{insight.distilled}"
      </p>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
        <button
          onClick={() => onUpvote(insight.id)}
          style={{
            display: 'flex', alignItems: 'center', gap: '5px',
            background: upvoted ? 'rgba(45,139,135,0.10)' : 'rgba(0,0,0,0.04)',
            border: `1px solid ${upvoted ? 'rgba(45,139,135,0.30)' : 'rgba(0,0,0,0.08)'}`,
            borderRadius: '20px', padding: '5px 12px',
            fontSize: '12px', fontWeight: 600,
            color: upvoted ? DARK : '#8B9B97',
            cursor: 'pointer', transition: 'all 0.15s',
          }}
        >
          <span>{upvoted ? '♥' : '♡'}</span>
          <span>This resonates · {insight.upvotes + (upvoted ? 1 : 0)}</span>
        </button>
      </div>
    </div>
  );
}

// ── Contribute modal ──────────────────────────────────────────────────────────
function ContributeModal({ programId, weekNumber, onClose, onContributed }) {
  const [text, setText]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState(null);

  const userId = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').user_id; } catch { return null; }
  })();

  const MIN_CHARS = 60;
  const canSubmit = text.trim().length >= MIN_CHARS && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !userId) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/circles/${programId}/contribute`, {
        user_id: userId,
        text: text.trim(),
        week_number: weekNumber,
      });
      if (res.data.contributed) {
        setResult(res.data.distilled);
        onContributed?.();
      } else {
        setError(res.data.reason || 'Could not contribute. Please try again.');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong.');
    }
    setSubmitting(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(10,30,28,0.55)',
      zIndex: 200,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      backdropFilter: 'blur(4px)',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#F5EFE8',
        borderRadius: '24px 24px 0 0',
        padding: '28px 24px 40px',
        width: '100%', maxWidth: '640px',
        maxHeight: '85vh', overflowY: 'auto',
      }}>
        {!result ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
              <div>
                <div style={{ fontSize: '11px', fontWeight: 700, color: TEAL, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Contribute to the Circle
                </div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1A2E2C' }}>
                  Share a distilled insight
                </h3>
              </div>
              <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', color: '#8B9B97', cursor: 'pointer' }}>×</button>
            </div>

            <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#5A7A76', lineHeight: 1.6 }}>
              Write something from your experience this week — a realisation, a shift, something that surprised you. HYPSEYA will extract the universally resonant core and share it anonymously. Your words will never appear verbatim.
            </p>

            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="This week I noticed that…"
              rows={5}
              style={{
                width: '100%', padding: '14px',
                border: `1.5px solid ${text.trim().length >= MIN_CHARS ? 'rgba(30,107,104,0.4)' : 'rgba(184,211,207,0.5)'}`,
                borderRadius: '12px', fontSize: '14px', lineHeight: 1.7,
                background: 'rgba(255,255,255,0.9)', color: '#1E3A39',
                resize: 'vertical', fontFamily: 'inherit', outline: 'none',
                boxSizing: 'border-box', marginBottom: '10px',
              }}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <span style={{ fontSize: '11px', color: text.trim().length >= MIN_CHARS ? TEAL : '#A0B4B0' }}>
                {text.trim().length < MIN_CHARS
                  ? `${MIN_CHARS - text.trim().length} more characters`
                  : '✓ Ready to contribute'}
              </span>
              <span style={{ fontSize: '11px', color: '#8B9B97' }}>Fully anonymous after processing</span>
            </div>

            {error && (
              <div style={{ background: 'rgba(163,59,43,0.07)', border: '1px solid rgba(163,59,43,0.20)',
                borderRadius: '10px', padding: '10px 14px', marginBottom: '14px',
                fontSize: '13px', color: '#8B2C1E' }}>
                {error}
              </div>
            )}

            <button onClick={handleSubmit} disabled={!canSubmit} style={{
              width: '100%', padding: '14px',
              background: canSubmit ? `linear-gradient(135deg, ${DARK}, ${TEAL})` : 'rgba(0,0,0,0.08)',
              color: canSubmit ? '#fff' : '#A0A0A0',
              border: 'none', borderRadius: '14px',
              fontSize: '14px', fontWeight: 600,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              boxShadow: canSubmit ? '0 4px 14px rgba(30,107,104,0.25)' : 'none',
            }}>
              {submitting ? 'Processing…' : 'Contribute to the Circle →'}
            </button>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>✦</div>
            <h3 style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: 700, color: '#1A2E2C' }}>
              Contributed
            </h3>
            <div style={{
              background: 'rgba(30,107,104,0.06)', border: '1px solid rgba(30,107,104,0.15)',
              borderRadius: '14px', padding: '18px', marginBottom: '20px',
              fontStyle: 'italic', fontSize: '15px', lineHeight: 1.7, color: '#2C4A47',
            }}>
              "{result}"
            </div>
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#6B8B87', lineHeight: 1.6 }}>
              This is what others in your program will see. Thank you for contributing to the collective field.
            </p>
            <button onClick={onClose} style={{
              background: `linear-gradient(135deg, ${DARK}, ${TEAL})`,
              color: '#fff', border: 'none', borderRadius: '14px',
              padding: '12px 28px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
            }}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function IntegrationCircles() {
  const navigate  = useNavigate();
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedWeek, setSelectedWeek]       = useState(null);
  const [insights, setInsights]               = useState([]);
  const [loading, setLoading]                 = useState(false);
  const [upvoted, setUpvoted]                 = useState({});   // { [insightId]: true }
  const [showContribute, setShowContribute]   = useState(false);
  const [refreshKey, setRefreshKey]           = useState(0);

  // Get user's enrolled programs to pre-select
  const [userEnrollments, setUserEnrollments] = useState([]);
  const [enrollmentsLoaded, setEnrollmentsLoaded] = useState(false);
  const userId = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').user_id; } catch { return null; }
  })();

  useEffect(() => {
    if (userId) fetchEnrollments();
  }, [userId]);

  const fetchEnrollments = async () => {
    try {
      const res = await axios.get(`${API}/programs/enrollment/${userId}`);
      const enrs = res.data.enrollments || [];
      setUserEnrollments(enrs);
      if (enrs.length > 0 && !selectedProgram) {
        setSelectedProgram(enrs[0].program_id);
        setSelectedWeek(enrs[0].current_week || 1);
      }
    } catch (e) {}
    setEnrollmentsLoaded(true);
  };

  useEffect(() => {
    if (selectedProgram) fetchInsights();
  }, [selectedProgram, selectedWeek, refreshKey]);

  const fetchInsights = async () => {
    setLoading(true);
    setInsights([]);
    try {
      const params = new URLSearchParams({ limit: '12' });
      if (selectedWeek) params.set('week_number', selectedWeek);
      const res = await axios.get(`${API}/circles/${selectedProgram}/insights?${params}`);
      setInsights(res.data.insights || []);
    } catch (e) {}
    setLoading(false);
  };

  const handleUpvote = async (insightId) => {
    if (upvoted[insightId]) return;
    setUpvoted(prev => ({ ...prev, [insightId]: true }));
    try {
      await axios.post(`${API}/circles/${selectedProgram}/insights/${insightId}/upvote`);
    } catch (e) {
      setUpvoted(prev => ({ ...prev, [insightId]: false }));
    }
  };

  const meta = selectedProgram ? PROGRAM_META[selectedProgram] : null;
  const userWeek = userEnrollments.find(e => e.program_id === selectedProgram)?.current_week;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #EEF5F4 0%, #F5EFE8 60%, #EBE4DA 100%)',
      padding: '0 0 80px',
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        background: `linear-gradient(135deg, ${DARK}, ${TEAL})`,
        padding: '56px 24px 40px',
        marginBottom: '24px',
      }}>
        <div style={{ maxWidth: '640px', margin: '0 auto' }}>
          <button onClick={() => navigate('/programs')} style={{
            background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '10px',
            color: '#fff', padding: '8px 14px', fontSize: '13px',
            cursor: 'pointer', marginBottom: '24px',
          }}>← Programs</button>
          <div style={{
            display: 'inline-block', background: 'rgba(255,255,255,0.15)',
            borderRadius: '20px', padding: '3px 12px', fontSize: '11px',
            fontWeight: 700, color: 'rgba(255,255,255,0.85)',
            letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px',
          }}>Integration Circles</div>
          <h1 style={{ margin: '8px 0 6px', fontSize: '28px', fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>
            You are not alone in this.
          </h1>
          <p style={{ margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.72)', lineHeight: 1.6 }}>
            Anonymous distilled insights from people going through the same program. No identities. No stories. Just what resonated.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '0 20px' }}>

        {/* Not enrolled — invite before showing the browse UI */}
        {enrollmentsLoaded && userEnrollments.length === 0 && (
          <div style={{
            background: 'rgba(255,255,255,0.85)',
            border: '1px solid rgba(30,107,104,0.14)',
            borderRadius: '18px', padding: '18px 20px', marginBottom: '20px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
          }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#1A2E2C', marginBottom: '3px' }}>
                You're not in a program yet
              </div>
              <div style={{ fontSize: '12px', color: '#6B8B87', lineHeight: 1.5 }}>
                Circles are the anonymous voices of each 8-week program. Join one to contribute — or browse below.
              </div>
            </div>
            <button onClick={() => navigate('/programs')} style={{
              flexShrink: 0,
              background: `linear-gradient(135deg, ${DARK}, ${TEAL})`,
              color: '#fff', border: 'none', borderRadius: '12px',
              padding: '10px 18px', fontSize: '13px', fontWeight: 600,
              cursor: 'pointer', boxShadow: '0 3px 12px rgba(30,107,104,0.22)',
            }}>Explore programs →</button>
          </div>
        )}

        {/* Program selector */}
        <div style={{ marginBottom: '20px' }}>
          <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: 700, color: '#6B8B87',
            textTransform: 'uppercase', letterSpacing: '0.06em' }}>Select Program</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {Object.entries(PROGRAM_META).map(([id, m]) => {
              const isEnrolled = userEnrollments.some(e => e.program_id === id);
              const isSelected = selectedProgram === id;
              return (
                <button key={id} onClick={() => { setSelectedProgram(id); setSelectedWeek(null); }} style={{
                  padding: '8px 14px', borderRadius: '20px',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: isSelected ? m.color : 'rgba(255,255,255,0.7)',
                  color: isSelected ? '#fff' : '#4A6A66',
                  border: `1.5px solid ${isSelected ? m.color : 'rgba(0,0,0,0.08)'}`,
                  fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  transition: 'all 0.15s',
                  boxShadow: isSelected ? `0 2px 12px ${m.color}40` : 'none',
                }}>
                  <m.Icon size={14} color={isSelected ? '#fff' : m.color} />
                  {m.short}
                  {isEnrolled && <span style={{ opacity: 0.7 }}>·</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Week filter */}
        {selectedProgram && (
          <div style={{ marginBottom: '20px' }}>
            <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: 700, color: '#6B8B87',
              textTransform: 'uppercase', letterSpacing: '0.06em' }}>Week</p>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button onClick={() => setSelectedWeek(null)} style={{
                padding: '6px 14px', borderRadius: '14px',
                background: selectedWeek === null ? DARK : 'rgba(255,255,255,0.7)',
                color: selectedWeek === null ? '#fff' : '#6B8B87',
                border: `1px solid ${selectedWeek === null ? DARK : 'rgba(0,0,0,0.08)'}`,
                fontSize: '12px', fontWeight: 600, cursor: 'pointer',
              }}>All weeks</button>
              {[1,2,3,4,5,6,7,8].map(w => (
                <button key={w} onClick={() => setSelectedWeek(w)} style={{
                  padding: '6px 12px', borderRadius: '14px',
                  background: selectedWeek === w ? DARK : (userWeek === w ? 'rgba(30,107,104,0.10)' : 'rgba(255,255,255,0.7)'),
                  color: selectedWeek === w ? '#fff' : DARK,
                  border: `1px solid ${selectedWeek === w ? DARK : 'rgba(30,107,104,0.15)'}`,
                  fontSize: '12px', fontWeight: selectedWeek === w || userWeek === w ? 700 : 400,
                  cursor: 'pointer',
                }}>W{w}{userWeek === w ? ' ←' : ''}</button>
              ))}
            </div>
          </div>
        )}

        {/* Contribute CTA */}
        {selectedProgram && userWeek && (
          <div style={{
            background: `linear-gradient(135deg, rgba(30,107,104,0.06), rgba(184,144,106,0.06))`,
            border: '1px solid rgba(30,107,104,0.14)',
            borderRadius: '18px',
            padding: '18px 20px',
            marginBottom: '20px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px',
          }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#1A2E2C', marginBottom: '3px' }}>
                Add your voice to the Circle
              </div>
              <div style={{ fontSize: '12px', color: '#6B8B87', lineHeight: 1.5 }}>
                Share a distilled insight from your Week {userWeek} experience. Fully anonymous.
              </div>
            </div>
            <button onClick={() => setShowContribute(true)} style={{
              flexShrink: 0,
              background: `linear-gradient(135deg, ${DARK}, ${TEAL})`,
              color: '#fff', border: 'none', borderRadius: '12px',
              padding: '10px 18px', fontSize: '13px', fontWeight: 600,
              cursor: 'pointer', boxShadow: '0 3px 12px rgba(30,107,104,0.22)',
            }}>Contribute →</button>
          </div>
        )}

        {/* Insights */}
        {!selectedProgram && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#8B9B97' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>○</div>
            <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6 }}>
              Select a program above to see insights from your circle.
            </p>
          </div>
        )}

        {selectedProgram && loading && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#8B9B97' }}>
            <p style={{ margin: 0 }}>Loading insights…</p>
          </div>
        )}

        {selectedProgram && !loading && insights.length === 0 && (
          <div style={{
            background: CREAM, border: '1px solid rgba(30,107,104,0.10)',
            borderRadius: '20px', padding: '40px 28px', textAlign: 'center',
          }}>
            <div style={{ fontSize: '36px', marginBottom: '14px' }}>✦</div>
            <h3 style={{ margin: '0 0 10px', fontSize: '16px', fontWeight: 700, color: '#1A2E2C' }}>
              Be the first to contribute
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#6B8B87', lineHeight: 1.6 }}>
              No insights yet for {selectedWeek ? `Week ${selectedWeek} of ` : ''}{meta?.label}.
              If you're in this program, your experience could be exactly what someone else needs to hear.
            </p>
            {userWeek && (
              <button onClick={() => setShowContribute(true)} style={{
                background: `linear-gradient(135deg, ${DARK}, ${TEAL})`,
                color: '#fff', border: 'none', borderRadius: '14px',
                padding: '12px 24px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                boxShadow: '0 4px 14px rgba(30,107,104,0.25)',
              }}>Start the Circle →</button>
            )}
          </div>
        )}

        {insights.map(ins => (
          <InsightCard
            key={ins.id}
            insight={ins}
            onUpvote={handleUpvote}
            upvoted={!!upvoted[ins.id]}
          />
        ))}

        {/* Footer note */}
        {insights.length > 0 && (
          <p style={{ textAlign: 'center', fontSize: '11px', color: '#A0B4B0', marginTop: '8px', lineHeight: 1.6 }}>
            All insights are anonymous and distilled by AI. No identifying information is ever stored or shared.
          </p>
        )}
      </div>

      {/* Contribute modal */}
      {showContribute && selectedProgram && (
        <ContributeModal
          programId={selectedProgram}
          weekNumber={selectedWeek || userWeek || 1}
          onClose={() => setShowContribute(false)}
          onContributed={() => { setShowContribute(false); setRefreshKey(k => k + 1); }}
        />
      )}
    </div>
  );
}
